import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCustomCover } from "../index";
import { DEVICE_MODES, EDITOR_ELEMENTS } from "../model/constants";
import type { DeviceMode } from "../model/types";
import { PlatformThemeProvider } from "./PlatformThemeProvider";
import {
  CoverView,
  type CoverAlign,
  type CoverTextVariant,
  type CoverVerticalAlign,
} from "./cover";

const HORIZONTAL_ALIGNS: CoverAlign[] = ["left", "center", "right"];
const VERTICAL_ALIGNS: CoverVerticalAlign[] = ["top", "center", "bottom"];
const TEXT_VARIANTS: CoverTextVariant[] = [
  "header1",
  "header2",
  "header3",
  "subtitle1",
  "subtitle2",
  "subtitle3",
];

const toAlign = (value: string | undefined): CoverAlign =>
  HORIZONTAL_ALIGNS.includes(value as CoverAlign)
    ? (value as CoverAlign)
    : "center";

const toVerticalAlign = (value: string | undefined): CoverVerticalAlign =>
  VERTICAL_ALIGNS.includes(value as CoverVerticalAlign)
    ? (value as CoverVerticalAlign)
    : "center";

const toTextVariant = (value: string | undefined): CoverTextVariant =>
  TEXT_VARIANTS.includes(value as CoverTextVariant)
    ? (value as CoverTextVariant)
    : "subtitle1";

const toTextColor = (value: string | undefined) =>
  value === "textSecondary" ? "textSecondary" : "textPrimary";

const toButtonsColor = (value: string | null) =>
  value === "secondary" ? "secondary" : "primary";

const toButtonsSize = (value: string | null) =>
  value === "small" || value === "large" ? value : "medium";

/**
 * Рамка устройства. Высоту обложке задаёт она: на витрине это 100vh, здесь
 * фиксированная пропорция, чтобы десктопный и мобильный режимы были похожи
 * на реальный экран.
 */
const DeviceFrame = styled("div", {
  shouldForwardProp: (prop) => prop !== "device",
})<{ device: DeviceMode }>(({ theme, device }) => ({
  width: "100%",
  maxWidth: device === "mobile" ? 430 : "100%",
  aspectRatio: device === "mobile" ? "9 / 13.5" : "16 / 6.5",
  overflow: "hidden",
  position: "relative",
  borderRadius: theme.shape.borderRadius,
}));

/**
 * Предпросмотр обложки. Внутри рамки действует тема платформы и те же
 * компоненты, что на витрине, снаружи остаётся тема кабинета.
 */
export const CustomCoverPreview: React.FC = () => {
  const { t } = useTranslation();
  const {
    title,
    subTitle,
    buttons,
    deviceMode,
    backgrounds,
    activeBackgrounds,
    brightness,
    buttonsColor,
    buttonsSize,
    align,
    alignMobile,
    verticalAlign,
    verticalAlignMobile,
    onChangeSelectedItem,
  } = useCustomCover();

  const device: DeviceMode =
    deviceMode === DEVICE_MODES.mobile
      ? DEVICE_MODES.mobile
      : DEVICE_MODES.desktop;
  const isMobile = device === DEVICE_MODES.mobile;

  const visibleButtons = buttons
    .filter((button) => button.text && !button.disabled)
    .map((button) => ({
      text: button.text || "",
      variant: button.variant,
    }));

  return (
    <Box display="flex" justifyContent="center">
      <DeviceFrame device={device}>
        <PlatformThemeProvider>
          <CoverView
            device={device}
            align={toAlign(isMobile ? alignMobile : align)}
            verticalAlign={toVerticalAlign(
              isMobile ? verticalAlignMobile : verticalAlign
            )}
            title={
              title.text
                ? {
                    text: title.text,
                    variant: toTextVariant(title.size),
                    color: toTextColor(title.color),
                    weight: title.weight,
                  }
                : undefined
            }
            subTitle={
              subTitle.text
                ? {
                    text: subTitle.text,
                    variant: toTextVariant(subTitle.size),
                    color: toTextColor(subTitle.color),
                    weight: subTitle.weight,
                  }
                : undefined
            }
            background={{
              desktopImageId: backgrounds.desktopImageId,
              mobileImageId: backgrounds.mobileImageId,
              desktopVideoId: backgrounds.desktopVideoId,
              mobileVideoId: backgrounds.mobileVideoId,
              brightness,
            }}
            buttons={visibleButtons}
            buttonsColor={toButtonsColor(buttonsColor)}
            buttonsSize={toButtonsSize(buttonsSize)}
            backgroundHint={
              activeBackgrounds?.id ? undefined : (
                <Typography variant="body2" color="text.secondary">
                  {t("platformCustomCovers.preview.uploadBackgroundHint")}
                </Typography>
              )
            }
            onSelectBackground={() =>
              onChangeSelectedItem(EDITOR_ELEMENTS.image)
            }
            onSelectTitle={() => onChangeSelectedItem(EDITOR_ELEMENTS.title)}
            onSelectSubTitle={() =>
              onChangeSelectedItem(EDITOR_ELEMENTS.subTitle)
            }
            onSelectButtons={() =>
              onChangeSelectedItem(EDITOR_ELEMENTS.buttons)
            }
          />
        </PlatformThemeProvider>
      </DeviceFrame>
    </Box>
  );
};
